import {
  Checkbox,
  CheckboxGroup,
  CheckboxProps,
  Input,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Control, Controller } from 'react-hook-form';
import { Modal } from '@ssar-app/uikit';
import { ClientContact } from '@ssar-app/domain';
import { Fragment } from 'react';

export type CheckboxFieldSource = {
  name: string;
  value: string;
  tooltip?: string;
};

export const getFormInputComponent = (
  name: string,
  control: Control,
  defaultValue: string | undefined,
  isReadOnly: boolean | undefined,
  placeHolder: string | undefined,
  label: string | undefined,
  onChangeCb?: (value: string) => void,
) => {
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ value, onChange }) => (
        <Tooltip hasArrow placement='top' label={label || ''}>
          <>
            {isReadOnly && <label>{value}</label>}
            {!isReadOnly && (
              <Input
                onChange={(e) => {
                  const enteredValue = e.target.value;
                  onChange(enteredValue);
                  if (onChangeCb) {
                    onChangeCb(enteredValue);
                  }
                }}
                value={value}
                placeholder={placeHolder}
                defaultValue={defaultValue}
                w='xl'
                h={8}
                spellCheck={false}
              />
            )}
          </>
        </Tooltip>
      )}
    ></Controller>
  );
};

export const getFormTextComponent = (
  name: string,
  control: Control,
  defaultValue?: string | undefined,
) => {
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ value }) => <Text mt={1}>{value}</Text>}
    ></Controller>
  );
};

export const getFormHiddenComponent = (
  name: string,
  control: Control,
  defaultValue?: string | undefined,
) => {
  return (
    <Controller control={control} name={name} defaultValue={defaultValue} />
  );
};

export const getFormCheckboxGroupComponent = (
  name: string,
  control: Control,
  defaultValue: string[] | undefined,
  fields: CheckboxFieldSource[],
  onChangeCb?: (values: string[]) => void,
  checkBoxProps?: CheckboxProps,
  addBRElement = true,
) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ value, onChange }) => (
        <CheckboxGroup
          defaultValue={value}
          onChange={(values) => {
            onChange(values);
            if (onChangeCb) {
              onChangeCb(values as string[]);
            }
          }}
        >
          {fields.map((field) => (
            <Fragment key={field.name}>
              <Checkbox
                value={field.value}
                mt={2}
                borderColor={'gray.300'}
                {...checkBoxProps}
              >
                <Tooltip placement='right' label={field.tooltip}>
                  {field.name}
                </Tooltip>
              </Checkbox>
              {addBRElement && <br></br>}
            </Fragment>
          ))}
        </CheckboxGroup>
      )}
    />
  );
};

export const getFormCheckboxComponent = (
  name: string,
  control: Control,
  defaultValue: boolean | undefined,
  displayName: string,
  onChangeCb?: (value: boolean) => void,
) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ value, onChange }) => (
        <Checkbox
          onChange={(e) => {
            const isSelected = e.target.checked;
            onChange(isSelected);
            if (onChangeCb) {
              onChangeCb(isSelected);
            }
          }}
          defaultChecked={defaultValue}
          isChecked={value}
          borderColor={'gray.300'}
        >
          {displayName}
        </Checkbox>
      )}
    ></Controller>
  );
};

export const showOverrideContactWarning = (
  newClientContact: ClientContact,
  contactCb: (clientContact: ClientContact) => void,
) => {
  Modal.confirm({
    header: 'Do you want to continue?',
    content:
      'You are about to overwrite the contact details with the selected contact. Do you want to continue?',
    headerProps: { pb: 4 },
    bodyProps: { pt: 0 },
    showCloseButton: false,
    cancelButtonProps: {
      size: 'sm',
      float: 'right',
      variant: 'solid',
      fontSize: 'sm',
      boxShadow: 'lg',
      bg: 'white',
      border: '1px',
      borderColor: 'gray.200',
      _hover: {
        background: 'white',
        color: 'blue.800',
        borderColor: 'blue.800',
      },
    },
    okButtonProps: { size: 'sm' },
    onOk() {
      if (contactCb) {
        contactCb(newClientContact);
      }
    },
  });
};
